// ---------------------------------------------------------------------------
// fieldGeo.js — client-side GeoJSON helpers for farm field boundaries.
//
// Small, dependency-free ports of api/src/geo.js so the boundary editor can
// derive acres / centroid / parcel attribution live as the user draws, and so
// the detection modal can frame captured imagery. Kept in sync with the backend
// (which re-derives authoritatively on save).
// ---------------------------------------------------------------------------

// Outer ring of a Polygon / first outer ring of a MultiPolygon, or null.
export function outerRing(geometry) {
  if (!geometry) return null;
  if (geometry.type === 'Polygon')      return geometry.coordinates?.[0] || null;
  if (geometry.type === 'MultiPolygon') return geometry.coordinates?.[0]?.[0] || null;
  return null;
}

// Centroid (mean of the outer ring) → { lat, lng } or null.
export function polygonCentroid(geometry) {
  const ring = outerRing(geometry);
  if (!ring || !ring.length) return null;
  let lat = 0, lng = 0;
  ring.forEach(c => { lng += c[0]; lat += c[1]; });
  return { lat: lat / ring.length, lng: lng / ring.length };
}

// Approximate area in acres (equirectangular projection around the mean
// latitude; 1 sq mile = 640 acres). Matches api/src/geo.js approxAreaSqMiles.
export function polygonAcres(geometry) {
  const rings = [];
  if (geometry?.type === 'Polygon')           rings.push(geometry.coordinates[0]);
  else if (geometry?.type === 'MultiPolygon') geometry.coordinates.forEach(p => rings.push(p[0]));
  else return 0;
  const R = 3958.8; // earth radius, miles
  let sqMi = 0;
  for (const ring of rings) {
    if (!ring || ring.length < 4) continue;
    const meanLat = ring.reduce((s, c) => s + c[1], 0) / ring.length;
    const cos = Math.cos(meanLat * Math.PI / 180);
    let a = 0;
    for (let i = 0; i < ring.length - 1; i++) {
      const x1 = ring[i][0]     * Math.PI / 180 * cos * R, y1 = ring[i][1]     * Math.PI / 180 * R;
      const x2 = ring[i + 1][0] * Math.PI / 180 * cos * R, y2 = ring[i + 1][1] * Math.PI / 180 * R;
      a += x1 * y2 - x2 * y1;
    }
    sqMi += Math.abs(a) / 2;
  }
  return sqMi * 640;
}

// Ray-casting point-in-ring test. pt = [lng, lat], ring = [[lng,lat], ...].
export function pointInRing(pt, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    const hit = ((yi > pt[1]) !== (yj > pt[1])) &&
      (pt[0] < ((xj - xi) * (pt[1] - yi)) / ((yj - yi) || 1e-12) + xi);
    if (hit) inside = !inside;
  }
  return inside;
}

// Which parcel (from a parcel-map FeatureCollection) contains [lng,lat]?
// Returns { parcelId, label } or null.
export function parcelForPoint(parcelsFC, lng, lat) {
  for (const f of parcelsFC?.features || []) {
    const g = f.geometry;
    const rings = g?.type === 'Polygon' ? [g.coordinates[0]]
      : g?.type === 'MultiPolygon' ? g.coordinates.map(p => p[0]) : [];
    if (rings.some(r => pointInRing([lng, lat], r))) {
      const p = f.properties || {};
      return { parcelId: p.parcelId || null, label: [p.parcelNumber || 'Parcel', p.county].filter(Boolean).join(' · ') };
    }
  }
  return null;
}

// Bounding box over all coordinates of a GeoJSON FeatureCollection.
// Returns { west, south, east, north } or null.
export function bboxOfFC(fc) {
  let w = Infinity, s = Infinity, e = -Infinity, n = -Infinity, any = false;
  const walk = (c) => {
    if (typeof c[0] === 'number') {
      any = true;
      if (c[0] < w) w = c[0]; if (c[0] > e) e = c[0];
      if (c[1] < s) s = c[1]; if (c[1] > n) n = c[1];
    } else { c.forEach(walk); }
  };
  (fc?.features || []).forEach(f => { if (f?.geometry?.coordinates) walk(f.geometry.coordinates); });
  return any ? { west: w, south: s, east: e, north: n } : null;
}

// Bounding box of a single geometry's coordinates. Returns {west,south,east,north} or null.
function bboxOfGeometry(geometry) {
  if (!geometry?.coordinates) return null;
  return bboxOfFC({ type: 'FeatureCollection', features: [{ type: 'Feature', geometry }] });
}

// Group parcel features into spatial clusters so that far-apart parcel groups
// are imaged separately (a single bbox over dispersed parcels zooms out so far
// that fields become unresolvable). Two parcels join the same cluster when
// their bounding boxes are within `gap` degrees of each other (default ~1.4mi);
// clustering is transitive (connected components), so a chain of adjacent
// parcels forms one cluster. Returns [{ bbox, count }] sorted largest-first.
export function clusterParcels(fc, gap = 0.02) {
  const boxes = (fc?.features || [])
    .map(f => bboxOfGeometry(f?.geometry))
    .filter(Boolean);
  const n = boxes.length;
  if (n === 0) return [];

  // Union-find over parcels whose (gap-expanded) bboxes overlap.
  const parent = boxes.map((_, i) => i);
  const find = (a) => { while (parent[a] !== a) { parent[a] = parent[parent[a]]; a = parent[a]; } return a; };
  const near = (A, B) =>
    !(A.east + gap < B.west || B.east + gap < A.west || A.north + gap < B.south || B.north + gap < A.south);
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (near(boxes[i], boxes[j])) parent[find(i)] = find(j);
    }
  }

  const groups = new Map();
  for (let i = 0; i < n; i++) {
    const r = find(i);
    if (!groups.has(r)) groups.set(r, []);
    groups.get(r).push(boxes[i]);
  }
  const clusters = [...groups.values()].map(bs => {
    let w = Infinity, s = Infinity, e = -Infinity, north = -Infinity;
    bs.forEach(b => { w = Math.min(w, b.west); s = Math.min(s, b.south); e = Math.max(e, b.east); north = Math.max(north, b.north); });
    return { bbox: { west: w, south: s, east: e, north }, count: bs.length };
  });
  // Largest groups first (most parcels) so a per-cluster cap keeps the biggest.
  return clusters.sort((a, b) => b.count - a.count);
}
